import { User, Trophy, ChevronUp, ChevronDown, ArrowLeftRight, Share2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getPhotoUrl } from "@/utils/photoUrl";

const COURT_POSITIONS = {
  1: { name: "Levantador", zone: "Fundo", color: "bg-cyan-500/20 text-cyan-300" },
  2: { name: "Ponteiro", zone: "Rede", color: "bg-orange-500/20 text-orange-300" },
  3: { name: "Central", zone: "Rede", color: "bg-purple-500/20 text-purple-300" },
  4: { name: "Oposto", zone: "Rede", color: "bg-red-500/20 text-red-300" },
  5: { name: "Ponteiro", zone: "Fundo", color: "bg-orange-500/20 text-orange-300" },
  6: { name: "Central", zone: "Fundo", color: "bg-purple-500/20 text-purple-300" }
};

function CourtPosition({ player, posNumber, isMvp, accentColor }) {
  if (!player) return null;
  const pos = COURT_POSITIONS[posNumber];
  return (
    <div className={`text-center p-3 rounded-xl ${isMvp ? 'bg-yellow-500/20 ring-2 ring-yellow-400' : 'bg-black/40'}`}>
      <div className="text-[10px] font-bold text-slate-400 mb-2">{posNumber}</div>
      {player.photo_url ? (
        <img src={getPhotoUrl(player.photo_url)} alt={player.name}
          className={`w-12 h-12 mx-auto rounded-full object-cover border-2 ${isMvp ? 'border-yellow-400' : 'border-white/20'}`} />
      ) : (
        <div className={`w-12 h-12 mx-auto rounded-full bg-slate-700 flex items-center justify-center border-2 ${isMvp ? 'border-yellow-400' : 'border-white/20'}`}>
          <User className="h-6 w-6 text-slate-400" />
        </div>
      )}
      <p className="text-sm font-bold mt-2 truncate text-white">{player.name?.split(' ')[0]}</p>
      <Badge className={`mt-1 ${pos?.color || ''} border-0 text-[10px]`}>{pos?.name}</Badge>
      {isMvp && <div className="text-yellow-400 text-[10px] mt-1">MVP</div>}
    </div>
  );
}

export default function TeamCard({
  team, teamPlayers, positions, maxAverage,
  teamNumber, getTeamColor, getTeamGradient, getTeamAccent,
  movePlayer, startSwapPlayer, shareOnWhatsApp, generatedTeams
}) {
  const accentColor = getTeamAccent(teamNumber);

  return (
    <div className={`rounded-2xl border-2 ${getTeamColor(teamNumber)} overflow-hidden`} data-testid={`team-card-${teamNumber}`}>
      <div className={`bg-gradient-to-b ${getTeamGradient(teamNumber)} p-6`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center font-black text-xl"
              style={{ backgroundColor: `${accentColor}20`, color: accentColor }}>
              {String(teamNumber).padStart(2, '0')}
            </div>
            <div>
              <h3 className="text-xl font-black">TIME {String(teamNumber).padStart(2, '0')}</h3>
              <p className="text-xs text-muted-foreground">
                Media: {(teamPlayers.reduce((acc, p) => acc + p.average, 0) / teamPlayers.length).toFixed(1)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {teamPlayers.length > 0 && (
              <Button variant="outline" size="sm" onClick={shareOnWhatsApp}
                className="border-green-500/50 text-green-400 hover:bg-green-500/10" data-testid={`share-whatsapp-${teamNumber}`}>
                <Share2 className="h-4 w-4 mr-1" />WhatsApp
              </Button>
            )}
          </div>
        </div>

        {/* Court Diagram */}
        {teamPlayers.length >= 6 && (
          <div className="bg-gradient-to-b from-green-900/40 to-green-800/30 rounded-xl p-4 border border-green-700/30 relative">
            <div className="text-center text-[10px] text-slate-500 uppercase tracking-widest pb-2">
              --- Rede ---
            </div>
            {/* Net positions */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <CourtPosition player={positions.p4} posNumber={4} isMvp={positions.p4?.average === maxAverage} accentColor={accentColor} />
              <CourtPosition player={positions.p3} posNumber={3} isMvp={positions.p3?.average === maxAverage} accentColor={accentColor} />
              <CourtPosition player={positions.p2} posNumber={2} isMvp={positions.p2?.average === maxAverage} accentColor={accentColor} />
            </div>
            {/* Back positions */}
            <div className="grid grid-cols-3 gap-3">
              <CourtPosition player={positions.p5} posNumber={5} isMvp={positions.p5?.average === maxAverage} accentColor={accentColor} />
              <CourtPosition player={positions.p6} posNumber={6} isMvp={positions.p6?.average === maxAverage} accentColor={accentColor} />
              <CourtPosition player={positions.p1} posNumber={1} isMvp={positions.p1?.average === maxAverage} accentColor={accentColor} />
            </div>
            <div className="text-center text-[10px] text-slate-500 uppercase tracking-widest pt-2">
              --- Fundo da Quadra ---
            </div>
          </div>
        )}

        {/* Player List with Controls */}
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Escalacao Completa</p>
            <p className="text-[10px] text-muted-foreground">Use ▲▼ para mover</p>
          </div>
          {teamPlayers.map((player, pIndex) => {
            const isMvp = player.average === Math.max(...teamPlayers.map(p => p.average));
            const courtPos = COURT_POSITIONS[pIndex + 1];
            return (
              <div key={player.id || pIndex}
                className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${isMvp ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-secondary/30 hover:bg-secondary/50'}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black text-sm flex-shrink-0 ${courtPos?.color || 'bg-slate-700'}`}>
                  {pIndex + 1}
                </div>
                <div className="flex flex-col gap-0.5 flex-shrink-0">
                  <button onClick={() => movePlayer(teamNumber, pIndex, 'up')} disabled={pIndex === 0}
                    className={`p-0.5 rounded hover:bg-white/10 transition-colors ${pIndex === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}>
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button onClick={() => movePlayer(teamNumber, pIndex, 'down')} disabled={pIndex === teamPlayers.length - 1}
                    className={`p-0.5 rounded hover:bg-white/10 transition-colors ${pIndex === teamPlayers.length - 1 ? 'opacity-30 cursor-not-allowed' : ''}`}>
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </div>
                <button onClick={() => startSwapPlayer(player, teamNumber, pIndex)}
                  className="p-1 rounded hover:bg-orange-500/20 transition-colors text-orange-400 flex-shrink-0">
                  <ArrowLeftRight className="h-4 w-4" />
                </button>
                {isMvp && <Trophy className="h-4 w-4 text-yellow-400 flex-shrink-0" />}
                {player.photo_url ? (
                  <img src={getPhotoUrl(player.photo_url)} alt={player.name}
                    className={`w-8 h-8 rounded-full object-cover border-2 flex-shrink-0 ${isMvp ? 'border-yellow-400' : ''}`}
                    style={{ borderColor: isMvp ? '' : accentColor }} />
                ) : (
                  <div className={`w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center border-2 flex-shrink-0 ${isMvp ? 'border-yellow-400' : ''}`}
                    style={{ borderColor: isMvp ? '' : accentColor }}>
                    <User className="h-4 w-4 text-primary" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-bold truncate ${isMvp ? 'text-yellow-300' : ''}`}>{player.name}</p>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">{player.position}</span>
                    {player.yankee && <span className="text-[10px] text-blue-400">{player.yankee}</span>}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className={`font-black text-lg ${player.average >= 8 ? 'text-green-400' : player.average >= 5 ? 'text-yellow-400' : 'text-red-400'}`}>
                    {player.average}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
